import { SyntheticEvent, useEffect, useLayoutEffect, useState } from "react";
import { CSSObject } from "@emotion/react";
import { IonBackdrop } from "@ionic/react";
import { type AxiosError } from "axios";
import { translate } from "components/string-table";
import {
  App,
  AuthLayout,
  BuildProfile,
  Colors,
  EmailField,
  EmailLoginErrorProps,
  ErrorLogin,
  ErrorResponseWithDataMessage,
  Icon,
  LoadingInterstitial,
  useAuth,
} from "main";
import { RouteComponentProps } from "react-router";
import { googleIconSVG } from "swing-assets";

import "./Login.module.css";

import { LogInButton } from "./LoginButton";

type LoginProps = {
  apiError?: AxiosError<ErrorResponseWithDataMessage>;
  app: App;
  build?: string;
  buildProfile?: BuildProfile;
  history: RouteComponentProps["history"];
  isWeb?: boolean;
  loginWithPasswordPath: string;
  signUpPath: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onLoginEmailSubmit: (email: string) => Promise<any>;
  platform: string;
  version?: string;
};

export function Login(props: LoginProps) {
  const {
    apiError,
    app,
    build,
    buildProfile,
    history,
    isWeb,
    loginWithPasswordPath,
    signUpPath,
    onLoginEmailSubmit,
    platform,
    version,
  } = props;

  /***** Hooks *****/
  const { signIn, isAuthenticating } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<AxiosError<EmailLoginErrorProps> | undefined>();
  const [_isValidEmail, _setIsValidEmail] = useState(false);
  const [_apiError, _setApiError] = useState(apiError);

  const search = window.location.search;

  useLayoutEffect(() => {
    // in addition to displaying the `apiError` we need to be able to clear it from within this component should
    // a user change their sign in method from Google to Cognito. Since useState only initializes state once
    // during initial render, subsequent changes to prop will not update useState so keeping in sync here
    _setApiError(apiError);
  }, [apiError]);

  const onEmailInput = (email: string) => {
    setEmail(email);
    if (isValidEmail(email)) {
      _setIsValidEmail(true);
    } else {
      _setIsValidEmail(false);
    }
  };

  const onSubmit = async (submittableEmail: string) => {
    const { provider, shouldSignUp } = await onLoginEmailSubmit(submittableEmail).catch((error) => {
      history.push({
        pathname: "/",
      });
      setError(error);
    });

    if (!provider) return;

    switch (provider) {
      case "COGNITO":
        setError(undefined);
        history.push({
          pathname: shouldSignUp ? signUpPath : loginWithPasswordPath,
          // Explicitly not using `useLocation` as we want to live value vs
          // cached value.
          search: window.location.search,
          state: { email: submittableEmail },
        });
        break;
      case "GOOGLE":
        // clear email error when error is displayed under email input and then user tries
        // another email that is recognized as Google and Google signin prompt is opened
        setError(undefined);
        signIn({ provider: provider.toLowerCase() }).catch((error) => {
          setError(error);
        });
        break;
    }
  };

  useEffect(() => {
    const urlEmail = new URLSearchParams(search).get("email");
    if (urlEmail) {
      // We need to set isValidEmail to `true` because landing on this page
      // via URL route params shouldn't require the user to delete something
      // first, then have the email validator run, then have the button
      // be submissible.
      _setIsValidEmail(true);
      setEmail(urlEmail);
      onSubmit(urlEmail);
    }
    // We only want this to run once - so exhaustive-deps are
    // okay here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleEmailSubmit(e: SyntheticEvent) {
    e.preventDefault();
    // clear Google error if it is displayed
    _setApiError(undefined);
    await onSubmit(email);
  }

  async function handleGoogleSubmit(e: SyntheticEvent) {
    e.preventDefault();
    // clear email error if it is displayed
    setError(undefined);
    // clear email input if it was provided
    setEmail("");
    await signIn({ provider: "google" });
  }

  return (
    <AuthLayout app={app} build={build} isWeb={isWeb} version={version}>
      {isAuthenticating ? (
        <div>
          <IonBackdrop visible />
          <div
            css={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 11,
              background: "var(--swing-white200)",
              width: "100%",
              height: "100%",
              borderRadius: 8,
              maxWidth: 360,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LoadingInterstitial />
          </div>
        </div>
      ) : (
        <>
          <form
            css={{ width: "100%", display: "flex", flexDirection: "column", gap: 32 }}
            onSubmit={async (e) => await handleEmailSubmit(e)}
          >
            <EmailField
              app={app}
              buildProfile={buildProfile}
              email={email}
              error={error}
              onEmailInput={(em) => onEmailInput(em)}
              platform={platform}
              provider="cognito"
              readOnly={false}
            />
            <LogInButton disabled={!_isValidEmail} isFluid hasShadow>
              <div css={styles.buttonContentContainer}>
                <span css={{ ...styles.buttonText, marginRight: 8 }}>
                  {translate("LOGIN_EMAIL_ENTRY_LOGIN_LABEL_ENTER_PASSWORD")}
                </span>
                <Icon
                  name="Arrow Right"
                  color={!_isValidEmail ? Colors.slate400 : Colors.white200}
                />
              </div>
            </LogInButton>
          </form>
          <Divider label={translate("LOGIN_EMAIL_ENTRY_LABEL_OR")} />
          <form css={{ width: "100%" }} onSubmit={async (e) => await handleGoogleSubmit(e)}>
            <LogInButton variant="outlined" isFluid>
              <div css={styles.buttonContentContainer}>
                <img src={googleIconSVG} alt="Login with Google" />
                <span css={{ ...styles.buttonText, marginLeft: "auto", marginRight: "auto" }}>
                  {translate("LOGIN_EMAIL_ENTRY_LOGIN_LABEL_CONTINUE_WITH_GOOGLE")}
                </span>
              </div>
            </LogInButton>
            {_apiError && (
              <ErrorLogin
                app={app}
                buildProfile={buildProfile}
                error={_apiError}
                platform={platform}
              />
            )}
          </form>
        </>
      )}
    </AuthLayout>
  );
}

type DividerProps = {
  label?: string;
};

function Divider(props: DividerProps) {
  const { label } = props;
  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        width: "100%",
        "&::before, &::after": {
          content: `""`,
          flex: 1,
          borderBottom: "1px solid var(--swing-gray-200)",
        },
        "&:not(:empty)::before": {
          marginRight: 12,
        },
        "&:not(:empty)::after": {
          marginLeft: 12,
        },
      }}
    >
      {label}
    </div>
  );
}

export function isValidEmail(email: string) {
  // 1 or more chars, then @ then 1 or more chars then . then 2 or more chars
  const emailRegex = new RegExp(/.+@.+\..{2,}/);
  return emailRegex.test(email);
}

const styles: Record<string, CSSObject> = {
  loginButton: {
    width: "100%",
    margin: 0,
    background: "transparent",
    overflow: "hidden",
    borderRadius: 50,
  },
  buttonContentContainer: {
    display: "flex",
    flexDirection: "row",
    placeItems: "center",
    justifyContent: "center",
  },
  buttonText: {
    fontFamily: "var(--swing-font-family-headers)",
  },
};
